import React, { useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
    SwipeableListItem as RawSwipeableListItem,
    LeadingActions,
    TrailingActions,
    SwipeAction as RawSwipeAction,
    Type,
} from 'react-swipeable-list';
import 'react-swipeable-list/dist/styles.css';
import '../../../../../generic/swipe/swipeable-overrides.css';
import {
    SWIPE_START_THRESHOLD,
    SCROLL_START_THRESHOLD,
} from '../../../../../generic/swipe/constants';
import { CardRow } from '../../../../../generic/ui/CardRow';
import { Mono } from '../../../../../generic/ui/Mono';
import { SwipeAction } from '../../../../../generic/ui/SwipeAction';
import OrderDeleteConfirm from '../../orders/Components/OrderDeleteConfirm';
import BarcodeIcon from '../../../../../generic/icons/IconLib/BarcodeIcon';
import PencilIcon from '../../../../../generic/icons/IconLib/PencilIcon';
import TrashIcon from '../../../../../generic/icons/IconLib/TrashIcon';
import PrintIcon from '../../../../../generic/icons/IconLib/PrintIcon';
import DivideIcon from '../../../../../generic/icons/IconLib/DivideIcon';
import { IEntriesKnisotItem } from '../../../../../modules/API/Knisot/models/entrie';
import { EXIST_PATH } from '../../../../../modules/API/settings';
import { useTranslation } from 'react-i18next';
import PackageBoxIcon from '../../../../../generic/icons/IconLib/PackageBoxIcon';
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon';

type Props = IEntriesKnisotItem & {
    isLast?: boolean;
    handleSelectItem?: (item: IEntriesKnisotItem) => void;
    print?: (entryNum: number | string, palletNum?: number) => void;
    selectForDivide?: (item: IEntriesKnisotItem) => void;
    handleDeleteItem?: (item: IEntriesKnisotItem) => void;
    onBadgeClick?: (item: IEntriesKnisotItem) => void;
    isOffice?: boolean;
    item: IEntriesKnisotItem;
};

const OVERLAY_THRESHOLD = 30; // % — open action overlay
const TRIGGER_THRESHOLD = 60; // % — fire primary action directly

const noop = () => {};

const KnisaLineRow: React.FC<Props> = ({
    DetailNum,
    EntryNum,
    ProductDesc,
    VarietyDesc,
    SizeDesc,
    Pack_Name,
    AmountPack,
    WeightNeto,
    Gross,
    GeneralProductID,
    GrowerName,
    Delivery_Num,
    Pallet_Num,
    PalletDetailRecID,
    Invoice,
    isLast = false,
    handleSelectItem,
    print,
    selectForDivide,
    handleDeleteItem,
    isOffice,
    item,
}) => {
    const [t] = useTranslation('knisa');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [editOverlay, setEditOverlay] = useState(false);
    const lastProgress = useRef(0);
    const lastDirection = useRef<'left' | 'right' | ''>('');

    const edgeColor: 'danger' | 'success' | 'none' = Delivery_Num !== 0 ? 'danger' : Pallet_Num !== 0 ? 'success' : 'none';
    const isInvoiced = Invoice !== 0;
    const canDelete = Delivery_Num === 0;
    const canDivide = !!isOffice && PalletDetailRecID === 0 && !isInvoiced;
    const canEditViaSwipe = !!isOffice && !isInvoiced;
    const canPrint = !!isOffice;

    const product = `${ProductDesc?.trim() ?? ''} ${VarietyDesc?.trim() ?? ''} ${SizeDesc?.trim() ?? ''}`.trim();
    const productImg = GeneralProductID
        ? `${EXIST_PATH}/images/products/${GeneralProductID}.png`
        : '/emptyBox.png';

    const handleEdit = () => {
        if (isInvoiced || !handleSelectItem) return;
        setEditOverlay(false);
        setConfirmDelete(false);
        handleSelectItem(item);
    };
    const handleDivide = () => {
        if (!canDivide) return;
        setEditOverlay(false);
        setConfirmDelete(false);
        selectForDivide?.(item);
    };
    const handleDeleteConfirm = () => {
        setConfirmDelete(false);
        handleDeleteItem?.(item);
    };
    const handlePrintClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        print?.(`${EntryNum}-${DetailNum}`, Pallet_Num !== 0 ? Pallet_Num : undefined);
    };

    const handleSwipeStart = () => {
        setEditOverlay(false);
        setConfirmDelete(false);
    };

    const handleSwipeProgress = (progress: number, direction: string) => {
        lastProgress.current = progress;
        if (direction === 'left' || direction === 'right') {
            lastDirection.current = direction;
        }
    };

    const handleSwipeEnd = () => {
        const p = lastProgress.current;
        const dir = lastDirection.current;
        lastProgress.current = 0;
        lastDirection.current = '';

        if (dir === 'right') {
            // Leading swipe → edit/divide overlay
            if (!canEditViaSwipe) return;
            if (p >= TRIGGER_THRESHOLD) {
                handleEdit();
                return;
            }
            if (p >= OVERLAY_THRESHOLD) {
                setConfirmDelete(false);
                setEditOverlay(true);
            }
            return;
        }
        if (dir === 'left') {
            // Trailing swipe → only opens delete confirm overlay; the actual
            // delete fires only when the user taps the confirm button.
            if (!canDelete) return;
            if (p >= OVERLAY_THRESHOLD) {
                setEditOverlay(false);
                setConfirmDelete(true);
            }
        }
    };

    // Library requires at least one action child to enable swiping —
    // we keep no-op SwipeActions and route real logic through onSwipeEnd
    // so we can apply two thresholds (overlay vs. direct fire).
    const leadingActions = canEditViaSwipe ? (
        <LeadingActions>
            <RawSwipeAction onClick={noop} Tag='div'>
                <DualIcon>
                    <PencilIcon />
                    {canDivide && <DivideIcon />}
                </DualIcon>
            </RawSwipeAction>
        </LeadingActions>
    ) : undefined;

    const trailingActions = canDelete ? (
        <TrailingActions>
            <RawSwipeAction onClick={noop} Tag='div'>
                <SwipeAction
                    variant='delete'
                    align='end'
                    icon={<TrashIcon />}
                    label={t('knisa_create_element_swipe_delete')}
                />
            </RawSwipeAction>
        </TrailingActions>
    ) : undefined;

    return (
        <Wrapper>
            <RawSwipeableListItem
                leadingActions={leadingActions}
                trailingActions={trailingActions}
                listType={Type.ANDROID}
                threshold={1.5}
                swipeStartThreshold={SWIPE_START_THRESHOLD}
                scrollStartThreshold={SCROLL_START_THRESHOLD}
                onSwipeStart={handleSwipeStart}
                onSwipeProgress={handleSwipeProgress}
                onSwipeEnd={handleSwipeEnd}
                blockSwipe={!isOffice}
            >
                {editOverlay && (
                    <EditConfirm>
                        <ConfirmBtn
                            type='button'
                            onClick={handleEdit}
                            aria-label={t('knisa_create_element_swipe_edit')}
                        >
                            <PencilIcon size={18} />
                        </ConfirmBtn>
                        {canDivide && (
                            <ConfirmBtn
                                type='button'
                                $variant='divide'
                                onClick={handleDivide}
                                aria-label='פצל'
                            >
                                <DivideIcon />
                            </ConfirmBtn>
                        )}
                    </EditConfirm>
                )}
                <Row $edge={edgeColor}>
                    <CardRow
                        $columns='minmax(7rem, 1.2fr) minmax(0, 1fr) auto 3.25rem'
                        $mobileColumns='minmax(6rem, 1.1fr) minmax(0, 1fr) auto 3rem'
                        $isLast={isLast}
                    >
                        <CardRow.Cell>
                            <CardRow.Stack $gap='0.25rem'>
                                <CardRow.Line $gap='0.5rem'>
                                    <ProductImg
                                        src={productImg}
                                        alt={product}
                                        loading='lazy'
                                        decoding='async'
                                        onError={(e) => {
                                            (e.currentTarget as HTMLImageElement).src = '/emptyBox.png';
                                        }}
                                    />
                                    <PrimaryName>{product}</PrimaryName>
                                </CardRow.Line>
                                <MetaLine>
                                    <MetaIcon>
                                        <BarcodeIcon color='currentColor' size={14} />
                                    </MetaIcon>
                                    <MetaText>
                                        <Mono>{EntryNum}-{DetailNum}</Mono>
                                    </MetaText>
                                </MetaLine>
                            </CardRow.Stack>
                        </CardRow.Cell>

                        <CardRow.Cell>
                            <CardRow.Stack $gap='0.25rem'>
                                <CardRow.Line $gap='0.5rem'>
                                    <SmallIcon $muted>
                                        <UserIcon color='currentColor' />
                                    </SmallIcon>
                                    <PrimaryName>{GrowerName?.trim()}</PrimaryName>
                                </CardRow.Line>
                                {Pack_Name && (
                                    <MetaLine>
                                        <MetaIcon>
                                            <PackageBoxIcon />
                                        </MetaIcon>
                                        <MetaText>
                                            {Pack_Name.trim()}
                                            {' · '}
                                            <Mono>{AmountPack}</Mono>
                                        </MetaText>
                                    </MetaLine>
                                )}
                            </CardRow.Stack>
                        </CardRow.Cell>

                        <CardRow.Cell $align='center'>
                            <CardRow.Stack $gap='0.125rem' $align='end'>
                                <WeightLine>
                                    <WeightLabel>נטו</WeightLabel>
                                    <Mono>{WeightNeto.toFixed(1)}</Mono>
                                </WeightLine>
                                <WeightLine $muted>
                                    <WeightLabel>ברוטו</WeightLabel>
                                    <Mono>{Gross ? Gross.toFixed(0) : '0'}</Mono>
                                </WeightLine>
                            </CardRow.Stack>
                        </CardRow.Cell>

                        <CardRow.Cell $align='center'>
                            <Actions onClick={(e) => e.stopPropagation()}>
                                {canPrint && (
                                    <IconBtn type='button' onClick={handlePrintClick} aria-label='print'>
                                        <PrintIcon />
                                    </IconBtn>
                                )}
                            </Actions>
                        </CardRow.Cell>
                    </CardRow>
                </Row>
                {confirmDelete && <OrderDeleteConfirm onConfirm={handleDeleteConfirm} />}
            </RawSwipeableListItem>
        </Wrapper>
    );
};

export default React.memo(KnisaLineRow);

const Wrapper = styled.div`
    position: relative;
    width: 100%;

    .swipeable-list-item,
    .swipeable-list-item__content {
        width: 100%;
    }
`;

const Row = styled.div<{ $edge: 'danger' | 'success' | 'none' }>`
    position: relative;
    width: 100%;
    flex: 1;
    min-width: 0;
    ${({ $edge, theme }) =>
        $edge !== 'none' &&
        css`
            box-shadow: inset 3px 0 0 ${$edge === 'danger' ? theme.colors.danger : theme.colors.success};
        `}
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const EditConfirm = styled.div`
    flex-shrink: 0;
    align-self: stretch;
    display: inline-flex;
    animation: ${fadeIn} 0.18s ease-out;
`;

const DualIcon = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.625rem;
    width: 100%;
    height: 100%;
    padding: 0 1rem;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};

    svg {
        width: 1.125rem;
        height: 1.125rem;
    }
`;

const ConfirmBtn = styled.button<{ $variant?: 'divide' }>`
    flex-shrink: 0;
    align-self: stretch;
    width: 4.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: ${({ theme, $variant }) =>
        $variant === 'divide' ? theme.colors.accentDeep : theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    cursor: pointer;
    user-select: none;
    transition:
        transform ${({ theme }) => theme.motion.fast} ease,
        filter ${({ theme }) => theme.motion.fast} ease;

    &:hover {
        filter: brightness(0.95);
    }

    &:active {
        transform: scale(0.97);
    }

    svg {
        width: 1.125rem;
        height: 1.125rem;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 3.5rem;
    }
`;

const ProductImg = styled.img`
    width: 1.5rem;
    height: 1.5rem;
    object-fit: contain;
    border-radius: 0.5rem;
    user-select: none;
    pointer-events: none;
    flex-shrink: 0;

    /* @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 2.25rem;
        height: 2.25rem;
    } */
`;

const SmallIcon = styled.span<{ $muted?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $muted }) => ($muted ? theme.colors.ink3 : theme.colors.ink2)};
    flex-shrink: 0;

    svg {
        width: 1rem;
        height: 1rem;
    }
`;

const PrimaryName = styled.span`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
    font-family: ${({ theme }) => theme.fonts.sans};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const MetaLine = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
`;

const MetaIcon = styled.span`
    color: ${({ theme }) => theme.colors.ink3};
    display: inline-flex;
    align-items: center;

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`;

const MetaText = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const WeightLine = styled.span<{ $muted?: boolean }>`
    display: inline-flex;
    align-items: baseline;
    gap: 0.375rem;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme, $muted }) => ($muted ? theme.text.sm : theme.text.md)};
    color: ${({ theme, $muted }) => ($muted ? theme.colors.ink3 : theme.colors.ink)};
    font-weight: ${({ $muted }) => ($muted ? 400 : 600)};
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme, $muted }) =>
            $muted ? theme.textMobile.sm : theme.textMobile.md};
    }
`;

const WeightLabel = styled.span`
    color: ${({ theme }) => theme.colors.ink4};
    font-size: ${({ theme }) => theme.text.xs};
    font-weight: 500;
    text-transform: none;
`;

const Actions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
`;

const IconBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: 1px solid ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    cursor: pointer;
    border-radius: 0.5rem;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem;
        height: 1rem;
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accentDeep};
        border-color: ${({ theme }) => theme.colors.accentDeep};
    }

    &:active {
        transform: translateY(1px);
    }
`;
